import React, { useState, useMemo } from 'react';
import { useRooms } from '../../context/RoomContext';
import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { Download, FileSpreadsheet, Calendar, Filter, Search, Eye } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

type DateRange = 'all' | 'today' | 'week' | 'month' | 'year' | 'custom';

interface ReportData {
  guestName: string;
  email: string;
  phone: string;
  roomName: string;
  bookingId: string;
  bookingDate: string;
  checkInDate: string;
  checkOutDate: string;
  totalAmount: number;
  paidAmount: number;
  paymentMethod: string;
  paymentDate: string;
  dueAmount: number;
  bookingStatus: string;
  source: string;
}

const AdminReports: React.FC = () => {
  const { bookings, rooms } = useRooms();
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Generate report data
  const reportData = useMemo(() => {
    const reportRows: any[] = [];

    bookings.forEach(booking => {
      const room = rooms.find(r => r.id === booking.roomId);
      const payments = booking.payments || [];
      const totalPaidForBooking = payments.reduce((sum, p) => sum + (p?.amount || 0), 0);
      const dueAmount = booking.totalPrice - totalPaidForBooking;
        // Sort payments by date to show chronological order and calculate running totals
        const sortedPayments = [...payments].sort((a, b) => 
          new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        
        let runningPaidAmount = 0;
        
        if (payments.length > 0) {
          sortedPayments.forEach((payment, index) => {
            runningPaidAmount += payment.amount;
            const runningDueAmount = booking.totalPrice - runningPaidAmount;
            
            reportRows.push({
              guestName: index === 0 ? booking.userName || 'N/A' : '', // Only show on first row
              email: index === 0 ? booking.userEmail || 'N/A' : '',
              phone: index === 0 ? booking.userPhone || 'N/A' : '',
              roomName: index === 0 ? room?.name || 'Unknown Room' : '',
              bookingId: index === 0 ? `IFA-${booking.id.substring(0, 8)}` : '',
              bookingDate: index === 0 ? format(parseISO(booking.createdAt), 'dd/MM/yyyy') : '',
              checkInDate: index === 0 ? format(parseISO(booking.checkIn), 'dd/MM/yyyy') : '',
              checkOutDate: index === 0 ? format(parseISO(booking.checkOut), 'dd/MM/yyyy') : '',
              totalAmount: index === 0 ? booking.totalPrice : '', // Only show on first row
              paidAmount: payment.amount, // Individual payment amount
              dueAmount: runningDueAmount, // Show running due amount after each payment
              bookingStatus: index === 0 ? booking.status : '',
              source: index === 0 ? booking.source || 'Manual' : '',
              paymentMethod: payment.method,
              paymentDate: payment.date ? format(parseISO(payment.date), 'dd/MM/yyyy') : 'N/A',
              isFirstPaymentRow: index === 0,
              bookingIdForGrouping: booking.id // For grouping purposes
            });
          });
        } else {
          // Create one row for bookings without payments
          reportRows.push({
            guestName: booking.userName || 'N/A',
            email: booking.userEmail || 'N/A',
            phone: booking.userPhone || 'N/A',
            roomName: room?.name || 'Unknown Room',
            bookingId: `IFA-${booking.id.substring(0, 8)}`,
            bookingDate: format(parseISO(booking.createdAt), 'dd/MM/yyyy'),
            checkInDate: format(parseISO(booking.checkIn), 'dd/MM/yyyy'),
            checkOutDate: format(parseISO(booking.checkOut), 'dd/MM/yyyy'),
            totalAmount: booking.totalPrice,
            paidAmount: 0,
            dueAmount: booking.totalPrice,
            bookingStatus: booking.status,
            source: booking.source || 'Manual',
            paymentMethod: 'No Payment',
            paymentDate: 'N/A',
            isFirstPaymentRow: true,
            isLastPaymentRow: true,
            bookingIdForGrouping: booking.id
          });
        }
    });

    return reportRows;
  }, [bookings, rooms]);

  // Filter data based on criteria
  const filteredData = useMemo(() => {
    let filtered = reportData;

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      let startDate: Date;
      let endDate: Date = endOfDay(now);

      switch (dateRange) {
        case 'today':
          startDate = startOfDay(now);
          break;
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        case 'custom':
          if (customStartDate && customEndDate) {
            startDate = startOfDay(parseISO(customStartDate));
            endDate = endOfDay(parseISO(customEndDate));
          } else {
            startDate = startOfDay(now);
          }
          break;
        default:
          startDate = startOfDay(now);
      }

      filtered = filtered.filter(item => {
        // Handle both payment date and booking date filtering
        let dateToCheck: Date;
        
        if (item.paymentDate && item.paymentDate !== 'N/A') {
          // For payment rows, use payment date
          dateToCheck = parseISO(item.paymentDate.split('/').reverse().join('-'));
        } else {
          // For booking rows without payments, use booking date
          dateToCheck = parseISO(item.bookingDate.split('/').reverse().join('-'));
        }
        
        return isWithinInterval(dateToCheck, { start: startDate, end: endDate });
      });
    }

    // Room filter
    if (selectedRoom !== 'all') {
      filtered = filtered.filter(item => {
        const room = rooms.find(r => r.name === item.roomName);
        return room?.id === selectedRoom;
      });
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(item => item.bookingStatus === selectedStatus);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.guestName.toLowerCase().includes(term) ||
        item.email.toLowerCase().includes(term) ||
        item.phone.toLowerCase().includes(term) ||
        item.roomName.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [reportData, dateRange, customStartDate, customEndDate, selectedRoom, selectedStatus, searchTerm, rooms]);

  // Export to Excel
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredData.map(item => ({
        'Booking ID': item.bookingId,
        'Guest Name': item.guestName,
        'Email': item.email,
        'Phone': item.phone,
        'Room Name': item.roomName,
        'Booking Date': item.bookingDate,
        'Check-in Date': item.checkInDate,
        'Check-out Date': item.checkOutDate,
        'Total Booking Value': item.totalAmount,
        'Paid Amount': item.paidAmount,
        'Payment Method': item.paymentMethod,
        'Payment Date': item.paymentDate,
        'Due Amount': item.dueAmount,
        'Status': item.bookingStatus
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Guest Report');

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const fileName = `guest-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    saveAs(data, fileName);
  };

  // Calculate summary statistics
  const summary = useMemo(() => {
    // Get unique bookings for proper calculations
    const uniqueBookingIds = new Set();
    const uniqueBookings: any[] = [];
    
    filteredData.forEach(item => {
      if (item.bookingIdForGrouping && !uniqueBookingIds.has(item.bookingIdForGrouping)) {
        uniqueBookingIds.add(item.bookingIdForGrouping);
        uniqueBookings.push(item);
      }
    });
    
    const totalBookings = uniqueBookings.length;
    const totalRevenue = uniqueBookings.reduce((sum, item) => sum + (typeof item.totalAmount === 'number' ? item.totalAmount : 0), 0);
    const totalPaid = filteredData.reduce((sum, item) => sum + item.paidAmount, 0);
    const totalDue = uniqueBookings.reduce((sum, item) => {
      const bookingPayments = filteredData.filter(row => row.bookingIdForGrouping === item.bookingIdForGrouping);
      const bookingTotalPaid = bookingPayments.reduce((paidSum, row) => paidSum + row.paidAmount, 0);
      const bookingDue = (typeof item.totalAmount === 'number' ? item.totalAmount : 0) - bookingTotalPaid;
      return sum + bookingDue;
    }, 0);

    return { totalBookings, totalRevenue, totalPaid, totalDue };
  }, [filteredData]);

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div className="mb-4 sm:mb-0">
          <h2 className="text-xl font-semibold text-gray-800">Guest Reports</h2>
          <p className="text-sm text-gray-500">Comprehensive booking and payment reports</p>
        </div>

        <button
          onClick={exportToExcel}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          <Download className="h-4 w-4 mr-2" />
          Export to Excel
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as DateRange)}
              className="form-select"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="year">Last Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Room Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
            <select
              value={selectedRoom}
              onChange={(e) => setSelectedRoom(e.target.value)}
              className="form-select"
            >
              <option value="all">All Rooms</option>
              {rooms.map(room => (
                <option key={room.id} value={room.id}>{room.name}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="form-select"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input-with-icon"
                placeholder="Search guests..."
              />
            </div>
          </div>
        </div>

        {/* Custom Date Range */}
        {dateRange === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="form-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="form-input"
              />
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
              <FileSpreadsheet className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">Total Records</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalBookings}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${summary.totalRevenue}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
              <Download className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">Amount Paid</p>
              <p className="text-2xl font-bold text-green-600">${summary.totalPaid}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-red-500 rounded-md p-3">
              <Eye className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">Amount Due</p>
              <p className="text-2xl font-bold text-red-600">${summary.totalDue}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Report Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Guest Report ({filteredData.length} records)
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-green-500">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Booking ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Guest Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Room Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Booking Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Check-in Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Check-out Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Total Booking Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Paid
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Payment Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Payment Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Due
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">
                      {item.bookingId || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.guestName || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.email || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.phone || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.roomName || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.bookingDate || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.checkInDate || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.checkOutDate || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {typeof item.totalAmount === 'number' ? `$${item.totalAmount}` : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      ${item.paidAmount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.paymentMethod}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.paymentDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                      {typeof item.dueAmount === 'number' ? `$${item.dueAmount}` : ''}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={13} className="px-6 py-4 text-center text-sm text-gray-500">
                    No records found matching the selected criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;